import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

const MIN_MOVE_THRESHOLD = 10;
const PAGINATION_WINDOW_SIZE_DEFAULT = 4;
const DEFAULT_MAX_OFFSET = 100;
const OFFSET_FACTOR = 0.5;
const TOUCH_OPTS = { passive: false };

/**
 * Reusable hook for horizontal swipe/drag (touch + mouse) with visual feedback.
 * Use on any container where you want swipe-to-navigate (e.g. carousels, pagination).
 *
 * @param {Object} options
 * @param {(startX: number, endX: number, startY: number | null, endY: number | null) => void} options.onSwipe - Called when a swipe gesture ends with meaningful horizontal movement. Use to change page/index.
 * @param {number} [options.maxOffset=100] - Max pixels for visual drag offset.
 * @param {string} [options.ignoreSelector] - CSS selector to ignore (e.g. '.pagination-dot'). Touch/mouse down on matching elements won't start drag.
 * @returns {{ ref: function, handlers: Object, swipeOffset: number, swipeDirection: string | null }} - Attach ref to the swipeable element (merge with your ref if needed); spread handlers onto it; use swipeOffset/swipeDirection for transform/opacity.
 */
export function useSwipe(options = {}) {
  const {
    onSwipe,
    maxOffset = DEFAULT_MAX_OFFSET,
    ignoreSelector = null,
  } = options;

  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const onSwipeRef = useRef(onSwipe);
  onSwipeRef.current = onSwipe;

  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);
  const touchStartYRef = useRef(null);
  const elementRef = useRef(null);

  const mouseStartRef = useRef(null);
  const mouseEndRef = useRef(null);
  const mouseStartYRef = useRef(null);

  const resetTouch = useCallback(() => {
    touchStartRef.current = null;
    touchEndRef.current = null;
    touchStartYRef.current = null;
  }, []);

  const resetMouse = useCallback(() => {
    mouseStartRef.current = null;
    mouseEndRef.current = null;
    mouseStartYRef.current = null;
    setIsDragging(false);
  }, []);

  const resetVisual = useCallback(() => {
    setSwipeOffset(0);
    setSwipeDirection(null);
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (ignoreSelector && e.target.closest(ignoreSelector)) return;
    const touch = e.targetTouches[0];
    if (!touch) return;
    touchEndRef.current = null;
    touchStartRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    resetVisual();
  }, [ignoreSelector, resetVisual]);

  const handleTouchMove = useCallback((e) => {
    const startX = touchStartRef.current;
    const startY = touchStartYRef.current;
    if (startX === null || startY === null) return;

    const touch = e.targetTouches[0];
    if (!touch) return;
    const currentX = touch.clientX;
    const currentY = touch.clientY;
    const deltaX = currentX - startX;
    const deltaY = Math.abs(currentY - startY);

    if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > MIN_MOVE_THRESHOLD) {
      e.preventDefault();
      const limitedOffset = Math.max(-maxOffset, Math.min(maxOffset, deltaX * OFFSET_FACTOR));
      setSwipeOffset(limitedOffset);
      setSwipeDirection(deltaX > 0 ? 'right' : 'left');
    }
    touchEndRef.current = currentX;
  }, [maxOffset]);

  const handleTouchEnd = useCallback((e) => {
    const startX = touchStartRef.current;
    const startY = touchStartYRef.current;
    const lastX = touchEndRef.current;
    const changedTouch = e.changedTouches?.[0];
    const endX = lastX ?? changedTouch?.clientX ?? null;
    const endY = changedTouch?.clientY ?? null;

    if (startX != null && endX != null) {
      const moved = Math.abs(startX - endX) > MIN_MOVE_THRESHOLD;
      if (moved && typeof onSwipeRef.current === 'function') {
        onSwipeRef.current(startX, endX, startY, endY);
      }
      setTimeout(() => { resetVisual(); }, 0);
    } else {
      setTimeout(() => { resetVisual(); }, 0);
    }
    resetTouch();
  }, [resetTouch, resetVisual]);

  const handleMouseDown = useCallback((e) => {
    if (ignoreSelector && e.target.closest(ignoreSelector)) return;
    setIsDragging(true);
    mouseEndRef.current = null;
    mouseStartRef.current = e.clientX;
    mouseStartYRef.current = e.clientY;
    resetVisual();
  }, [ignoreSelector, resetVisual]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const startX = mouseStartRef.current;
    const startY = mouseStartYRef.current;
    if (startX == null || startY == null) return;

    const currentX = e.clientX;
    const currentY = e.clientY;
    const deltaX = currentX - startX;
    const deltaY = Math.abs(currentY - startY);

    mouseEndRef.current = currentX;
    if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 5) {
      e.preventDefault();
      const limitedOffset = Math.max(-maxOffset, Math.min(maxOffset, deltaX * OFFSET_FACTOR));
      setSwipeOffset(limitedOffset);
      setSwipeDirection(deltaX > 0 ? 'right' : 'left');
    }
  }, [isDragging, maxOffset]);

  const handleMouseUp = useCallback((e) => {
    if (!isDragging) return;
    const startX = mouseStartRef.current;
    const endX = mouseEndRef.current;
    const startY = mouseStartYRef.current;
    const endY = e.clientY;

    if (startX != null && endX != null) {
      const moved = Math.abs(startX - endX) > MIN_MOVE_THRESHOLD;
      if (moved && typeof onSwipeRef.current === 'function') {
        onSwipeRef.current(startX, endX, startY, endY);
        e.preventDefault();
      }
      setTimeout(() => { resetVisual(); }, 0);
    } else {
      setTimeout(() => { resetVisual(); }, 0);
    }
    resetMouse();
  }, [isDragging, resetMouse, resetVisual]);

  const handleMouseLeave = useCallback(() => {
    resetMouse();
    resetVisual();
  }, [resetMouse, resetVisual]);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const setRef = useCallback((el) => {
    const prev = elementRef.current;
    if (prev === el) return;
    if (prev) {
      prev.removeEventListener('touchstart', handleTouchStart, TOUCH_OPTS);
      prev.removeEventListener('touchmove', handleTouchMove, TOUCH_OPTS);
      prev.removeEventListener('touchend', handleTouchEnd, TOUCH_OPTS);
      prev.removeEventListener('touchcancel', handleTouchEnd, TOUCH_OPTS);
    }
    elementRef.current = el;
    if (el) {
      el.addEventListener('touchstart', handleTouchStart, TOUCH_OPTS);
      el.addEventListener('touchmove', handleTouchMove, TOUCH_OPTS);
      el.addEventListener('touchend', handleTouchEnd, TOUCH_OPTS);
      el.addEventListener('touchcancel', handleTouchEnd, TOUCH_OPTS);
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  useEffect(() => {
    return () => {
      const el = elementRef.current;
      if (el) {
        el.removeEventListener('touchstart', handleTouchStart, TOUCH_OPTS);
        el.removeEventListener('touchmove', handleTouchMove, TOUCH_OPTS);
        el.removeEventListener('touchend', handleTouchEnd, TOUCH_OPTS);
        el.removeEventListener('touchcancel', handleTouchEnd, TOUCH_OPTS);
      }
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    ref: setRef,
    handlers: {
      onMouseDown: handleMouseDown,
      onMouseLeave: handleMouseLeave,
    },
    swipeOffset,
    swipeDirection,
  };
}

/**
 * Returns a sliding window of page indices for pagination (e.g. [0,1,2,3] or [3,4,5,6]).
 * Going forward: window slides when current page hits the last slot (e.g. 4th bar).
 * Going backward: window moves so current page is visible.
 *
 * @param {number} totalPages - Total number of pages
 * @param {number} currentPage - Current page index (0-based)
 * @param {number} [windowSize=4] - Number of dots/slots to show
 * @returns {number[]} Array of page indices to display
 */
export function usePaginationWindow(totalPages, currentPage, windowSize = PAGINATION_WINDOW_SIZE_DEFAULT) {
  const previousPageRef = useRef(currentPage);

  return useMemo(() => {
    const total = totalPages;
    const previousPage = previousPageRef.current;
    const step = windowSize - 1;

    if (total <= windowSize) {
      previousPageRef.current = currentPage;
      return Array.from({ length: total }, (_, i) => i);
    }

    let startIndex;
    if (currentPage < previousPage) {
      // Going backward: window so current page is visible
      startIndex = currentPage < step ? 0 : step * Math.floor(currentPage / windowSize);
    } else {
      // Going forward: slide when active hits last bar in window
      startIndex = currentPage < step ? 0 : step * Math.floor((currentPage + 1) / windowSize);
    }
    if (startIndex + windowSize > total) {
      startIndex = Math.max(0, total - windowSize);
    }
    previousPageRef.current = currentPage;

    return Array.from({ length: windowSize }, (_, i) => startIndex + i).filter((i) => i < total);
  }, [totalPages, currentPage, windowSize]);
}

export default useSwipe;
